import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AlertTriangle, CheckCircle2, ChevronDown, LockKeyhole, ReceiptText, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContentUltraPriority, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type {
  ComposicaoProposta,
  PlanoCNPJ,
  RenovacaoFlag,
  RenovacaoSimulacao,
} from '@/constants/planosCNPJ';
import { calcularTotalGB, calcularTotalValor, PLANOS_RENOVACAO_CNPJ as PLANOS_RENOVACAO_CNPJ_STATIC } from '@/constants/planosCNPJ';
import { usePlanosPropostaCatalogo, type PlanoCatalogoCategoria } from '@/hooks/usePlanosPropostaCatalogo';
import {
  useRenovacaoParametros,
  type RenovacaoFaixaParametro,
} from '@/hooks/useRenovacaoParametros';
import { deltasDaRenovacao, normalizarMsisdn } from '@/lib/renovacaoCalculo';
import { renovacaoExigeAparelho, renovacaoMantemPacote, tmDaRenovacao } from '@/lib/carteiraClaro/calcularAptidaoCarteira';
import { tmPelaFidelidade } from '@/lib/fidelidadeConta';
import {
  assinaturaFaturaRenovacao,
  reconciliarRenovacaoComFatura,
  renovacaoEstaSincronizadaComFatura,
  type LinhaFaturaRenovacao,
} from '@/lib/renovacaoProposta';
import { cn } from '@/lib/utils';
import { ComparativoLinhasPlano } from './ComparativoLinhasPlano';

interface Props {
  cnpj: string;
  composicao: ComposicaoProposta | null;
  linhasFatura?: LinhaFaturaRenovacao[];
  faturaUploadId?: string | null;
  tempoContratoMeses?: number | null;
  /** classificação da carteira Claro: o CG renova sem aparelho já no M15 */
  classificacao?: string | null;
  /** true quando o TM veio da carteira importada (fonte oficial p/ liberar renovação) */
  tmOrigemCarteira?: boolean;
  reconciliarComAFaturaInteira?: boolean;
  userRole?: string | null;
  onComposicaoChange: (composicao: ComposicaoProposta) => void;
  tipoPessoa?: 'PF' | 'PJ';
}

const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function SeletorFlag({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: RenovacaoFlag;
  disabled: boolean;
  onChange: (value: RenovacaoFlag) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={(next) => onChange(next as RenovacaoFlag)} disabled={disabled}>
        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContentUltraPriority>
          <SelectItem value="SIM">Sim</SelectItem>
          <SelectItem value="NAO">Não</SelectItem>
          <SelectItem value="NAO_INFORMADO">Não informado</SelectItem>
        </SelectContentUltraPriority>
      </Select>
    </div>
  );
}

export function RenovacaoComissionamentoPanel({
  cnpj,
  composicao,
  linhasFatura = [],
  reconciliarComAFaturaInteira = true,
  faturaUploadId,
  tempoContratoMeses,
  classificacao,
  tmOrigemCarteira,
  userRole,
  onComposicaoChange,
  tipoPessoa = 'PJ',
}: Props) {
  const mesAtual = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit' });
  const meta = useMemo<RenovacaoSimulacao>(() => composicao?.renovacao || ({
    flagForaAreaCabeada: 'NAO_INFORMADO',
    flagBlFixaAtiva: 'NAO_INFORMADO',
    flagConvergenciaAto: 'NAO_INFORMADO',
    pedidoFixaVinculadoId: null,
    faturaUploadId: faturaUploadId || null,
    mesCompetencia: mesAtual,
    modoAgrupamento: 'POR_LINHA',
  }), [composicao?.renovacao, faturaUploadId, mesAtual]);
  const { data: parametros, isLoading, error } = useRenovacaoParametros(meta.mesCompetencia, cnpj);
  const { withCatalogo } = usePlanosPropostaCatalogo(tipoPessoa);
  const ehCpf = tipoPessoa === 'PF';
  // ⛔ O catálogo de renovação do CPF é `renov` — o `movel_renovacao` PF está
  // todo inativo e deixava o seletor vazio (Douglas 25/09/2026).
  const planosRenovacao = useMemo(
    () => ehCpf
      ? withCatalogo('renov' as PlanoCatalogoCategoria, PLANOS_RENOVACAO_CNPJ_STATIC)
      : withCatalogo('movel_renovacao', PLANOS_RENOVACAO_CNPJ_STATIC),
    [withCatalogo, ehCpf],
  );
  const ultimaSincronizacaoRef = useRef<string | null>(null);
  const [enquadramentoAberto, setEnquadramentoAberto] = useState(false);
  const [faixaFatEstimativa, setFaixaFatEstimativa] = useState<number>(1000);
  // a tabela "Renovação por Delta da linha" das Regras de Produtividade:
  // a faixa de faturamento escolhe a linha; o Delta escolhe F1–F7
  const { data: matrizRenovacao } = useQuery({
    queryKey: ['renovacao-matriz-consultor-lead'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data: rules, error } = await supabase
        .from('commission_rules')
        .select('id, faixa_faturamento, competencia')
        .eq('cargo', 'CONSULTOR')
        .eq('ativo', true)
        .is('usuario_id', null)
        .order('competencia', { ascending: false });
      if (error) throw error;
      const porFaixa = new Map<number, { id: string; faixa_faturamento: number }>();
      for (const r of (rules || []) as Array<{ id: string; faixa_faturamento: number }>) {
        if (!porFaixa.has(r.faixa_faturamento)) porFaixa.set(r.faixa_faturamento, r);
      }
      const vigentes = [...porFaixa.values()].sort((a, b) => a.faixa_faturamento - b.faixa_faturamento);
      const ids = vigentes.map((r) => r.id);
      if (ids.length === 0) return { faixasFat: [] as number[], pct: new Map<string, number>() };
      const { data: celulas, error: e2 } = await supabase
        .from('commission_rule_renovacao_delta' as never)
        .select('commission_rule_id, faixa_delta, percentual')
        .in('commission_rule_id', ids);
      if (e2) throw e2;
      const pct = new Map<string, number>();
      for (const c of (celulas || []) as Array<{ commission_rule_id: string; faixa_delta: number; percentual: number }>) {
        const rule = vigentes.find((r) => r.id === c.commission_rule_id);
        if (rule) pct.set(`${rule.faixa_faturamento}:${c.faixa_delta}`, Number(c.percentual));
      }
      return { faixasFat: vigentes.map((r) => r.faixa_faturamento), pct };
    },
  });
  const linhas = useMemo(() => composicao?.linhas.filter((linha) => linha.tipo === 'RENOVACAO') || [], [composicao]);
  const podeEditarEnquadramento = ['master', 'diretor', 'bko'].includes(userRole || '');
  const sugestoesPorMsisdn = useMemo(
    () => new Map(linhasFatura.map((linha) => [normalizarMsisdn(linha.numero), linha])),
    [linhasFatura],
  );

  const assinaturaSincronizacao = useMemo(() => {
    const catalogo = planosRenovacao.map((plano) => `${plano.id}:${plano.gb}:${plano.valor}`).join(';');
    return `${assinaturaFaturaRenovacao(cnpj, faturaUploadId, linhasFatura)}|tm:${Number(tempoContratoMeses || 0)}|${catalogo}`;
  }, [cnpj, faturaUploadId, linhasFatura, planosRenovacao, tempoContratoMeses]);

  useEffect(() => {
    if (ehCpf || !reconciliarComAFaturaInteira || linhasFatura.length === 0) return;
    const jaSincronizada = renovacaoEstaSincronizadaComFatura(
      composicao,
      linhasFatura,
      meta.linhasExcluidas,
    );
    // ⛔ UMA reconciliacao por assinatura (fatura + catalogo + TM), nao "ate'
    // sincronizar". O `jaSincronizada &&` que existia aqui fazia o efeito
    // reconciliar a cada render sempre que o predicado nao fechasse — e ele nao
    // fecha, por exemplo, quando dois numeros da fatura normalizam igual. Cada
    // volta emitia composicao nova: e' a tela tremendo ao mexer na proposta
    // (Douglas 01/09/2026). Depois de reconciliada, a edicao do consultor manda.
    if (ultimaSincronizacaoRef.current === assinaturaSincronizacao) return;
    if (jaSincronizada) {
      ultimaSincronizacaoRef.current = assinaturaSincronizacao;
      return;
    }
    const reconciliada = reconciliarRenovacaoComFatura(
      composicao,
      linhasFatura,
      planosRenovacao,
      { ...meta, faturaUploadId: faturaUploadId || meta.faturaUploadId },
      tempoContratoMeses,
      classificacao,
      { sugerirPlano: false },
    );
    if (!reconciliada) return;
    ultimaSincronizacaoRef.current = assinaturaSincronizacao;
    onComposicaoChange(reconciliada);
  }, [ehCpf, reconciliarComAFaturaInteira, assinaturaSincronizacao, composicao, faturaUploadId, linhasFatura, meta, onComposicaoChange, planosRenovacao, tempoContratoMeses, classificacao]);

  useEffect(() => {
    if (!composicao || linhas.length === 0) return;
    const precisaInicializarMeta = !composicao.renovacao;
    const precisaInicializarLinha = linhas.some((linha) =>
      linha.receitaRenovada === undefined
      || linha.receitaAdicionada === undefined
      || (Boolean(linha.planoId) && !linha.planoNovo)
      || linha.ticketNovo === undefined,
    );
    if (!precisaInicializarMeta && !precisaInicializarLinha) return;

    onComposicaoChange({
      ...composicao,
      renovacao: meta,
      linhas: composicao.linhas.map((linha) => {
        if (linha.tipo !== 'RENOVACAO') return linha;
        const receitaRenovada = linha.receitaRenovada ?? linha.ticketNovo ?? linha.plano?.valor ?? 0;
        const receitaAdicionada = linha.receitaAdicionada ?? 0;
        return {
          ...linha,
          planoNovo: linha.planoNovo || linha.plano?.nome || linha.planoId || '',
          receitaRenovada,
          receitaAdicionada,
          ticketNovo: receitaRenovada + receitaAdicionada,
        };
      }),
    });
  }, [composicao, linhas, meta, onComposicaoChange]);

  if (!composicao || linhas.length === 0) {
    return (
      <Card className="bg-card border border-border border-l-[3px] border-l-primary rounded-r-lg rounded-l-none">
        <CardContent className="p-4 text-sm text-muted-foreground">
          {ehCpf ? 'Adicione as linhas de renovação na aba Proposta.' : linhasFatura.length > 0 ? 'Preparando o simulador com as linhas da última fatura…' : 'Importe a fatura para iniciar o simulador por linha.'}
        </CardContent>
      </Card>
    );
  }

  const atualizarMeta = (patch: Partial<RenovacaoSimulacao>) => {
    onComposicaoChange({ ...composicao, renovacao: { ...meta, ...patch } });
  };

  const atualizarLinha = (linhaId: string, patch: { plano?: PlanoCNPJ; receitaRenovada?: number; receitaAdicionada?: number }) => {
    const linhasAtualizadas = composicao.linhas.map((linha) => {
      if (linha.id !== linhaId) return linha;
      const trocouPlano = Boolean(patch.plano);
      const plano = patch.plano ?? linha.plano;
      const receitaRenovada = trocouPlano ? Number(plano?.valor || 0) : patch.receitaRenovada ?? linha.receitaRenovada ?? plano?.valor ?? 0;
      const receitaAdicionada = trocouPlano ? 0 : patch.receitaAdicionada ?? linha.receitaAdicionada ?? 0;
      const sugestaoRenovacao = linha.sugestaoRenovacao
        ? {
            ...linha.sugestaoRenovacao,
            aplicadaAutomaticamente: plano?.id === linha.sugestaoRenovacao.planoId
              && receitaRenovada === linha.sugestaoRenovacao.ticketNovo
              && receitaAdicionada === 0,
          }
        : undefined;
      return {
        ...linha,
        planoId: plano?.id ?? linha.planoId,
        plano,
        planoNovo: plano?.nome || linha.planoNovo || linha.planoId || '',
        receitaRenovada,
        receitaAdicionada,
        ticketNovo: receitaRenovada + receitaAdicionada,
        sugestaoRenovacao,
      };
    });
    onComposicaoChange({
      ...composicao,
      renovacao: meta,
      linhas: linhasAtualizadas,
      totalGB: calcularTotalGB(linhasAtualizadas, linhasAtualizadas.some((linha) => linha.tipo === 'FIBRA')),
      totalValor: calcularTotalValor(linhasAtualizadas),
    });
  };

  const sinal = parametros?.deltaFormulaSign || 'ECONOMICO';
  const faixasDelta = (parametros?.faixas || []).filter((f: RenovacaoFaixaParametro) => Number(f.tabela) === 1);
  const faixaDoDeltaLinha = (delta: number | null) => {
    if (delta === null) return null;
    return faixasDelta.find((f) =>
      (f.delta_min === null || delta >= Number(f.delta_min))
      && (f.delta_max === null || delta <= Number(f.delta_max)),
    )?.faixa ?? null;
  };
  const pctDaFaixa = (faixa: number | null) =>
    faixa === null ? null : matrizRenovacao?.pct.get(`${faixaFatEstimativa}:${faixa}`) ?? null;
  const mesesContrato = ehCpf ? 0 : Number(tempoContratoMeses || 0);
  const elegivel12 = ehCpf || mesesContrato >= 12;
  const temAparelhoNaNegociacao = (composicao?.linhas || []).some((linha) => Boolean(linha.aparelho));
  const numerosRenovados = linhas.map((linha) => String(linha.numero || ''));
  const renovadasSet = new Set(numerosRenovados.map(normalizarMsisdn));
  const regraTm = tmDaRenovacao({
    tmCarteira: mesesContrato,
    parcial: linhasFatura.some((linha) => !renovadasSet.has(normalizarMsisdn(linha.numero))),
    numerosRenovados,
    linhasConta: linhasFatura,
  });
  const mesesRegra = ehCpf ? 0 : Number(regraTm.tm || 0);
  const tmPelasLinhas = !ehCpf && regraTm.origem === 'fidelidade';
  const restricaoPacoteM12M15 = !ehCpf && renovacaoMantemPacote(mesesRegra, classificacao);
  const exigeAparelhoM12M15 = !ehCpf && renovacaoExigeAparelho(mesesRegra, classificacao);
  const avaliadas = linhas.map((linha) => {
    const daConta = sugestoesPorMsisdn.get(normalizarMsisdn(linha.numero));
    const atual = Number(linha.valorAtual || (ehCpf ? daConta?.valorAtual : 0) || 0);
    const renovada = Number(linha.receitaRenovada ?? linha.plano?.valor ?? 0);
    const deltas = linha.planoId && linha.plano
      ? deltasDaRenovacao(atual, renovada)
      : { economico: null, literal: null };
    const gbAtual = Number(linha.gbAtual ?? daConta?.gbAtual ?? 0);
    const gbNovo = Number(linha.plano?.gb || 0);
    const pacoteValido = !restricaoPacoteM12M15 || Boolean(linha.planoId && gbNovo >= gbAtual);
    const deltaAtivo = sinal === 'LITERAL_REGULAMENTO' ? deltas.literal : deltas.economico;
    const faixa = !ehCpf && linha.planoId ? faixaDoDeltaLinha(deltaAtivo) : null;
    const pctComissao = pctDaFaixa(faixa);
    const comissaoEstimada = pctComissao === null ? null : renovada * (pctComissao / 100);
    return { linha, daConta, atual, renovada, deltas, gbAtual, gbNovo, pacoteValido, faixa, pctComissao, comissaoEstimada };
  });

  const numerosFatura = linhasFatura.map((item) => normalizarMsisdn(item.numero));
  const msisdnsFatura = new Set(numerosFatura);
  const temFatura = numerosFatura.length > 0
    && numerosFatura.every((numero) => numero.length >= 10)
    && msisdnsFatura.size === numerosFatura.length;
  // ⛔ "renovacaoTotal" (exigir TODAS as linhas da fatura na proposta) foi
  // removido daqui — Douglas 03/09/2026, ver o Alert de bloqueio mais abaixo.
  const linhasComSugestao = avaliadas.filter((item) => item.linha.sugestaoRenovacao?.aplicadaAutomaticamente);
  const diferencaSugerida = linhasComSugestao.reduce((soma, item) => soma + (item.renovada - item.atual), 0);
  const comissaoSugerida = linhasComSugestao.every((item) => item.comissaoEstimada != null)
    ? linhasComSugestao.reduce((soma, item) => soma + Number(item.comissaoEstimada), 0)
    : null;
  const distribuicaoSugerida = [...linhasComSugestao.reduce((mapa, item) => {
    if (item.faixa != null) mapa.set(item.faixa, (mapa.get(item.faixa) || 0) + 1);
    return mapa;
  }, new Map<number, number>())].sort(([a], [b]) => a - b).map(([faixa, total]) => `F${faixa} ${total}`).join(' · ');
  const pedidoFixaVinculado = parametros?.pedidosFixa.find((item) => item.id === meta.pedidoFixaVinculadoId);
  const convergenciaPendente = Boolean(meta.pedidoFixaVinculadoId && pedidoFixaVinculado && !pedidoFixaVinculado.instalado);
  const convergenciaForaCompetencia = Boolean(
    meta.pedidoFixaVinculadoId
    && pedidoFixaVinculado?.instalado
    && pedidoFixaVinculado.dataAtivacao?.slice(0, 7) !== meta.mesCompetencia,
  );

  return (
    <Card className="relative overflow-hidden rounded-xl border border-border bg-card pl-1 shadow-[0_2px_10px_rgba(42,20,24,.08)]">
      <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]" />
      <CardHeader className="border-b border-border px-3.5 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-sm">
              <ReceiptText className="h-4 w-4 shrink-0 text-primary" />
              Simulador de renovação por linha
            </CardTitle>
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground" title="A fatura fornece plano e valor atuais. Entre M12 e M15, o pacote atual deve ser mantido; a partir de M16, todos os planos ativos ficam disponíveis.">
              {ehCpf ? `Renovação CPF · plano do catálogo CPF · ${linhasFatura.length > 0 ? 'atual pela conta CPF' : 'conta CPF opcional'} · sem TM` : 'Atual × novo por linha · M12–M15 mantém pacote · M16+ permite qualquer plano ativo'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!ehCpf && (() => {
              const tm = mesesRegra;
              if (tm <= 0) {
                return (
                  <span className="flex h-6 items-center gap-1 rounded-md border border-primary/45 bg-primary/5 px-2 text-[11px] font-bold text-primary dark:text-[#C87484]" title="O TM vem da carteira Claro importada — sem ele não dá para garantir que o CNPJ pode renovar.">
                    <AlertTriangle className="h-3 w-3" />sem TM na carteira · confirme antes de renovar
                  </span>
                );
              }
              const mantemPacote = renovacaoMantemPacote(tm, classificacao);
              const exigeAparelho = renovacaoExigeAparelho(tm, classificacao);
              const naoRenova = tm < 12;
              const livre = !naoRenova && !mantemPacote && !exigeAparelho;
              return (
                <span
                  className={cn('flex h-6 items-center gap-1 rounded-md border px-2 text-[11px] font-bold tabular-nums',
                    naoRenova
                      ? 'border-primary/45 bg-primary/5 text-primary dark:text-[#C87484]'
                      : livre
                        ? 'border-[#075E54]/45 bg-[#075E54]/[0.06] text-[#075E54] dark:text-[#25D366]'
                        : 'border-border bg-muted/40 text-foreground')}
                  title={tmPelasLinhas
                    ? `Renovação parcial: TM das linhas renovadas, pela fidelidade de cada uma na conta · média do CNPJ na carteira: M${mesesContrato}`
                    : `${tmOrigemCarteira ? 'TM da carteira Claro importada' : 'TM informado no lead (sem registro na carteira)'} · M12–M15 mantém o pacote · M12–M16 exige aparelho · CG livre já no M15 · M17+ livre`}
                >
                  {naoRenova && <AlertTriangle className="h-3 w-3" />}
                  TM M{tm}{tmPelasLinhas ? ' · pelas linhas renovadas' : !tmOrigemCarteira ? ' · fora da carteira' : ''} · {naoRenova ? 'ainda não renova (mínimo M12)' : livre ? 'qualquer plano ativo' : `${mantemPacote ? 'mantém pacote' : 'qualquer pacote'} · ${exigeAparelho ? 'exige aparelho' : 'sem aparelho obrigatório'}`}
                </span>
              );
            })()}
            <Badge className="h-6 shrink-0 rounded-md text-xs font-bold tabular-nums" variant="outline">{linhas.length} linhas</Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-2 p-3">
        {error && <Alert variant="destructive"><AlertDescription>{String(error.message || error)}</AlertDescription></Alert>}

        {linhasComSugestao.length > 0 && (
          <div className="relative flex h-12 items-center justify-between gap-3 overflow-hidden rounded-lg border border-border bg-muted/35 px-3 pl-4">
            <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]" />
            <div className="flex min-w-0 items-center gap-2">
              <Sparkles className="h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0">
                <p className="truncate text-xs font-bold text-foreground">Pré-preenchimento automático em {linhasComSugestao.length} linha(s)</p>
                <p className="truncate text-[10px] text-muted-foreground">Não é ordem para reduzir: compare e altere cada seletor. {distribuicaoSugerida}{comissaoSugerida == null ? '' : ` · comissão estimada ${moeda.format(comissaoSugerida)}`}</p>
              </div>
            </div>
            <div className="shrink-0 text-right tabular-nums">
              <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{diferencaSugerida < 0 ? 'Redução mensal preenchida' : diferencaSugerida > 0 ? 'Aumento mensal preenchido' : 'Sem alteração mensal'}</p>
              <p className={cn('text-sm font-bold', diferencaSugerida > 0 ? 'text-[#075E54] dark:text-[#25D366]' : diferencaSugerida < 0 ? 'text-primary dark:text-[#C87484]' : 'text-foreground')}>{diferencaSugerida > 0 ? '+' : ''}{moeda.format(diferencaSugerida)}</p>
            </div>
          </div>
        )}

        {!ehCpf && (<>
        <div className="overflow-hidden rounded-lg border border-border">
          <button
            type="button"
            className="flex h-[34px] w-full items-center justify-between gap-3 bg-card px-3 text-left hover:bg-muted/55"
            onClick={() => setEnquadramentoAberto((aberto) => !aberto)}
            aria-expanded={enquadramentoAberto}
          >
            <span className="flex min-w-0 items-center gap-2">
              <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', enquadramentoAberto && 'rotate-180')} />
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">Enquadramento e parâmetros</span>
            </span>
            <span className="truncate text-[10px] text-muted-foreground">
              delta {sinal === 'ECONOMICO' ? 'econômico' : 'literal'} · competência {meta.mesCompetencia}
            </span>
          </button>

          {enquadramentoAberto && (
            <div className="space-y-3 border-t border-border bg-muted/20 p-3">
              <div className="grid gap-3 md:grid-cols-3">
                <SeletorFlag label="Fora da área cabeada?" value={meta.flagForaAreaCabeada} disabled={!podeEditarEnquadramento} onChange={(value) => atualizarMeta({ flagForaAreaCabeada: value })} />
                <SeletorFlag label="Já possui BL Fixa ativa?" value={meta.flagBlFixaAtiva} disabled={!podeEditarEnquadramento} onChange={(value) => atualizarMeta({ flagBlFixaAtiva: value })} />
                <SeletorFlag label="Convergência no mesmo mês?" value={meta.flagConvergenciaAto} disabled={!podeEditarEnquadramento} onChange={(value) => atualizarMeta({ flagConvergenciaAto: value })} />
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Pedido de Fixa vinculado</Label>
                  <Select
                    value={meta.pedidoFixaVinculadoId || 'SEM_VINCULO'}
                    disabled={!podeEditarEnquadramento}
                    onValueChange={(value) => {
                      const pedido = parametros?.pedidosFixa.find((item) => item.id === value);
                      const mesAtivacao = pedido?.dataAtivacao?.slice(0, 7);
                      atualizarMeta({
                        pedidoFixaVinculadoId: value === 'SEM_VINCULO' ? null : value,
                        flagConvergenciaAto: value === 'SEM_VINCULO'
                          ? 'NAO_INFORMADO'
                          : !pedido?.instalado ? 'NAO_INFORMADO' : mesAtivacao === meta.mesCompetencia ? 'SIM' : 'NAO',
                      });
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContentUltraPriority>
                      <SelectItem value="SEM_VINCULO">Sem vínculo</SelectItem>
                      {(parametros?.pedidosFixa || []).map((pedido) => (
                        <SelectItem key={pedido.id} value={pedido.id}>{pedido.numero} · {pedido.instalado ? 'instalado' : 'instalação pendente'}</SelectItem>
                      ))}
                    </SelectContentUltraPriority>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Itens no pedido</Label>
                  <div className="flex h-8 items-center rounded-md border border-border bg-card px-3 text-xs text-foreground">Um item para cada linha renovada</div>
                </div>
              </div>

              {!podeEditarEnquadramento && (
                <p className="flex items-center gap-1 text-[10px] text-muted-foreground"><LockKeyhole className="h-3 w-3" /> Leitura somente para consultores.</p>
              )}

              {/* Convenção do delta e cluster saíram desta tela (Douglas 29/08):
                  são parâmetros de sistema — o cálculo usa o valor vigente do banco. */}
            </div>
          )}
        </div>

        {convergenciaPendente && (
          <Alert><AlertTriangle className="h-4 w-4" /><AlertDescription>Convergência pendente — a Fixa vinculada ainda não está instalada. O enquadramento interno poderá mudar após a instalação.</AlertDescription></Alert>
        )}
        {convergenciaForaCompetencia && (
          <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>A Fixa vinculada foi instalada fora da competência da renovação e não comprova convergência no mesmo mês.</AlertDescription></Alert>
        )}
        {avaliadas.some((item) => !item.pacoteValido) && (
          <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>Entre M12 e M15, o novo plano deve manter ou aumentar o pacote de GB atual de cada linha. O valor mensal pode subir ou descer.</AlertDescription></Alert>
        )}

        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
          {[
            { ok: temFatura, label: 'Conta importada', detalhe: temFatura ? `${linhasFatura.length} linhas encontradas` : 'importe a conta na aba Fatura' },
            {
              ok: elegivel12,
              label: elegivel12 ? 'Base Claro há 12 meses' : 'Renova, mas não comissiona',
              detalhe: elegivel12
                ? `${mesesContrato} meses informados${tmPelasLinhas ? ` · linhas renovadas no M${mesesRegra}` : ''}${restricaoPacoteM12M15 ? ' · pacote atual obrigatório' : ''}`
                : `M${mesesContrato}: abaixo de M12 cai em faixa 7, sem comissão — o pedido passa`,
            },
            // ⛔ NAO E' MAIS REQUISITO (Douglas 03/09/2026): "retire essa regra
            // para que os consultores consigam apagar alguma linha da renovacao
            // na proposta e mandar o pedido... cliente tem 10 linhas, vamos
            // mandar 8... tem que liberar." Fica so' informativo — sempre "ok",
            // mostrando quantas linhas da fatura entraram na proposta.
            { ok: true, label: 'Linhas na proposta', detalhe: `${linhas.length} de ${linhasFatura.length} linhas da fatura` },
            ...(exigeAparelhoM12M15 ? [{
              ok: temAparelhoNaNegociacao,
              label: 'Aparelho na negociação',
              detalhe: temAparelhoNaNegociacao
                ? 'aparelho incluído na proposta'
                : `no M${mesesRegra}${tmPelasLinhas ? ' das linhas renovadas' : ''} só aprova com pelo menos um aparelho — encaixe na proposta`,
            }] : []),
          ].map((item) => (
            <div key={item.label} className={cn('flex h-10 items-center gap-2 rounded-lg border px-2.5', item.ok ? 'border-success/45' : 'border-primary/45')}>
              {item.ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-success" /> : <AlertTriangle className="h-4 w-4 shrink-0 text-primary" />}
              <div className="min-w-0"><p className="text-xs font-bold text-foreground">{item.label}</p><p className="truncate text-[10px] text-muted-foreground" title={item.detalhe}>{item.detalhe}</p></div>
            </div>
          ))}
        </div>
        </>)}

        <ComparativoLinhasPlano
          titulo="Renovação por linha"
          linhas={avaliadas.map((item) => ({
            id: item.linha.id,
            numero: item.linha.numero,
            contaNumero: item.linha.contaNumero,
            competenciaFatura: item.linha.competenciaFatura,
            planoAtual: item.linha.planoAtual || (ehCpf ? item.daConta?.planoAtual : '') || '',
            gbAtual: item.gbAtual,
            valorAtual: item.atual,
            planoId: item.linha.planoId,
            plano: item.linha.plano,
            valorNovo: item.renovada,
            delta: item.deltas.economico,
            pacoteValido: item.pacoteValido,
            faixa: item.faixa,
            percentualComissao: item.pctComissao,
            comissao: item.comissaoEstimada,
            tempoContratoMeses: ehCpf ? null : tmPelaFidelidade(item.daConta?.dataFidelidade) ?? (mesesContrato > 0 ? mesesContrato : null),
          }))}
          planos={planosRenovacao}
          mostrarComissao={!ehCpf}
          restricaoGbMinimo={restricaoPacoteM12M15}
          cabecalhoDireita={ehCpf ? undefined : (
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <span>comissão pela tabela de renovação · faixa de faturamento</span>
              <Select value={String(faixaFatEstimativa)} onValueChange={(v) => setFaixaFatEstimativa(Number(v))}>
                <SelectTrigger className="h-6 w-[92px] px-2 text-[11px] tabular-nums"><SelectValue /></SelectTrigger>
                <SelectContentUltraPriority>{(matrizRenovacao?.faixasFat || [1000]).map((f) => <SelectItem key={f} value={String(f)}>R$ {f.toLocaleString('pt-BR')}</SelectItem>)}</SelectContentUltraPriority>
              </Select>
              <span title="A faixa que vale é a do seu faturamento no fechamento do mês — esta é a referência da estimativa.">· a real é a do fechamento</span>
            </div>
          )}
          onPlanoChange={(linhaId, plano) => atualizarLinha(linhaId, { plano })}
          onValorChange={(linhaId, valor) => atualizarLinha(linhaId, { receitaRenovada: valor })}
        />

        {!ehCpf && !isLoading && !elegivel12 && (
          <Alert><AlertDescription>Cliente com M{mesesContrato} de base: a renovação pode ser fechada normalmente, mas entra em <strong>faixa 7 e não comissiona</strong> — só a partir de M12 paga.</AlertDescription></Alert>
        )}

        {!ehCpf && !isLoading && (!temFatura || (exigeAparelhoM12M15 && !temAparelhoNaNegociacao)) && (
          <Alert variant="destructive"><AlertDescription>O pedido fica bloqueado até concluir os requisitos: conta importada{exigeAparelhoM12M15 ? ` e pelo menos um aparelho na negociação (M${mesesRegra}${tmPelasLinhas ? ' das linhas renovadas' : ''})` : ''}.</AlertDescription></Alert>
        )}
      </CardContent>
    </Card>
  );
}
